import { isFailedTransaction } from '@/feature/transactions/components/helper';
import { SocialCurrency } from '@/hooks/use-social-currency';
import { formatSocialCurrency, toLocaleCurrency } from '@/utils/numeric/currency';
import { toTitleCase } from '@/utils/toTitleCase';
import { format } from 'date-fns';
import i18n from 'i18next';

import { TRANSACTION_REQUEST_STATUS } from '../const';
import { Transaction, TransactionRequestStatusType, TransactionStatus } from '../hooks/use-transaction-details';

/**
 * Display currency for a sweeps transaction: the gold coin, the green Betr Cash, or
 * the purple Bonus (Betr Bucks / play-through) icon. `null` is real-money (no icon).
 */
export type TransactionDisplayCurrency = SocialCurrency | 'BONUS';

/** Bonus amounts format like cash (2 decimals); only the icon differs. */
export const toAmountCurrency = (currency: TransactionDisplayCurrency): SocialCurrency =>
    currency === 'BONUS' ? SocialCurrency.CASH : currency;

export const getTransactionDisplayCurrency = (
    action: string,
    currency: string | null | undefined,
    hasSocialWallet: boolean
): TransactionDisplayCurrency | null => {
    if (!hasSocialWallet) {
        return null;
    }
    switch (action) {
        case 'coin_purchase':
            return SocialCurrency.COINS;
        case 'bonus':
        case 'fre_bonus':
            return 'BONUS';
        case 'cash_bonus':
        case 'withdrawal':
        case 'transfer_out':
            return SocialCurrency.CASH;
        default:
            return currency === 'BNS' ? SocialCurrency.CASH : SocialCurrency.COINS;
    }
};

/**
 * Format a transaction for display
 * @param transaction - Transaction object
 */
export const formatTransaction = (transaction?: Transaction) => {
    if (!transaction) {
        return null;
    }
    const hasSocialWallet = transaction.to === 'Betr Social Wallet';
    const displayCurrency = getTransactionDisplayCurrency(transaction.action, transaction.currency, hasSocialWallet);

    const isDepositOrWithdrawal =
        transaction.action === 'deposit' || transaction.action === 'withdrawal' || transaction.action === 'cash_bonus';

    const isIAP = transaction.action === 'coin_purchase' && transaction.transaction_id !== null;

    const shouldShowLimits = isDepositOrWithdrawal && !isIAP;

    const requestStatus = formatTransactionRequestStatus(transaction);
    const isCancelled = transaction.status === 'WITHDRAWAL_CANCELLED';
    const baseFields = {
        header: formatHeader(transaction, displayCurrency),
        initiated: formatInitiated(transaction),
        status: formatStatus(transaction),
        cost: transaction.cost ? `${toLocaleCurrency(transaction.cost)}` : undefined,
        beforeBalance: formatTransactionBalance(transaction.before_balance, displayCurrency),
        afterBalance: formatTransactionBalance(transaction.after_balance, displayCurrency),
        transactionId: transaction.transaction_id,
        isFailed: isFailedTransaction(transaction.status) && !isCancelled,
        isCancelled,
        requestStatus,
        // The multi-step stepper only applies to review flows (multiple steps) or in-progress
        // states; instant single-step completions render the plain "Completed – date" status.
        showStatusStepper:
            !isCancelled &&
            (requestStatus.length > 1 ||
                requestStatus.some(step => step.status === 'PENDING' || step.status === 'FAILED')),
        currency: transaction.currency,
        displayCurrency,
        ...(shouldShowLimits && {
            dailyDailyDepositLimitAfter: formatBalance(transaction.daily_deposit_limit_after),
            weeklyDepositLimitAfter: formatBalance(transaction.weekly_deposit_limit_after),
            monthlyDepositLimitAfter: formatBalance(transaction.monthly_deposit_limit_after),
        }),
    };

    switch (transaction.action) {
        case 'withdrawal':
            const withdrawalOrRedemption = hasSocialWallet ? 'Redemption' : 'Withdrawal';
            return {
                subText: `${withdrawalOrRedemption} to ${formatCardType(transaction)}${formatLast4(transaction)} from ${
                    transaction.to
                }`,
                from: transaction.to,
                to: formatCardDetails(transaction),
                ...baseFields,
            };
        case 'deposit':
            return {
                subText: `Deposit from ${formatCardType(transaction)}${formatLast4(transaction)} to ${transaction.to}`,
                from: formatCardDetails(transaction),
                to: transaction.to,
                ...baseFields,
            };
        case 'debit':
            return {
                subText: `Debit from ${transaction.to}`,
                from: transaction.to,
                to: '',
                ...baseFields,
            };
        case 'credit':
            return {
                subText: `Credit to ${transaction.to}`,
                from: '',
                to: transaction.to,
                ...baseFields,
            };
        case 'daily_top_up':
            return {
                subText: i18n.t('transaction_details:claimed_from_daily_bonus'),
                from: '',
                to: transaction.to,
                ...baseFields,
            };
        case 'crossover_top_up':
            return {
                subText: i18n.t('transaction_details:claimed_from_crossover'),
                from: '',
                to: transaction.to,
                ...baseFields,
            };
        case 'auto_top_up':
            return {
                subText: `Auto Top Up to ${transaction.to}`,
                from: '',
                to: transaction.to,
                ...baseFields,
            };
        case 'bonus':
        case 'fre_bonus': {
            const isCredit = transaction.status === 'CREDIT';
            const isFreeBetBonus = transaction.action === 'fre_bonus';
            const sourceLabel = isFreeBetBonus
                ? hasSocialWallet
                    ? i18n.t('wallet:social_bonus')
                    : 'Betr bucks'
                : 'Play-through cash';
            return {
                subText: `${sourceLabel} ${isCredit ? 'to' : 'removed from'} ${transaction.to}`,
                from: isCredit ? '' : transaction.to,
                to: isCredit ? transaction.to : '',
                ...baseFields,
            };
        }
        case 'cash_bonus':
        case 'coin_purchase':
            return {
                subText: `Purchase from ${formatCardType(transaction)}${formatLast4(transaction)} to ${transaction.to}`,
                from: formatCardDetails(transaction),
                to: transaction.to,
                ...baseFields,
            };
        case 'transfer_in':
        case 'transfer_out':
            return {
                subText: `Transfer from ${transaction.from} to ${transaction.to}`,
                from: transaction.from,
                to: transaction.to,
                ...baseFields,
            };
    }
};

/**
 * Format the Transaction Details header
 * @param transaction - Transaction object
 */
export const formatHeader = (transaction: Transaction, displayCurrency: TransactionDisplayCurrency | null) => {
    // On withdrawal cancellation, the amount should be displayed as negative.
    const isCancelledWithdrawal = transaction.action === 'withdrawal' && transaction.status === 'WITHDRAWAL_CANCELLED';
    const isNegative = isCancelledWithdrawal || transaction.amount < 0;
    const absAmount = Math.abs(transaction.amount);

    if (displayCurrency) {
        const formatted = formatSocialCurrency(absAmount, toAmountCurrency(displayCurrency));
        return isNegative ? `-${formatted}` : `+${formatted}`;
    }

    const currency = absAmount.toFixed(2);
    if (isNegative) {
        return `-${currency}`;
    }
    return transaction.amount > 0 ? `+${currency}` : currency;
};

/**
 * Format the `Initiated` row as `m/dd/yyyy at h:mm am/pm`
 * @param transaction - Transaction object
 */
export const formatInitiated = (transaction: Transaction) => formatDateTime(transaction.initiated);

/**
 * Format the `Status` row  as `{Status} - m/dd/yyyy at h:mm am/pm`
 * @param transaction - Transaction object
 */
export const formatStatus = (transaction: Transaction) =>
    `${formatStatusText(transaction.status)} – ${formatDateTime(transaction.created_at)}`;

/**
 * Format the currency rows to locale currency
 * @param value
 */
export const formatBalance = (value: string | null | number) => {
    return value === undefined || value === null ? undefined : toLocaleCurrency(value);
};

/**
 * Format a date to `m/dd/yyyy at h:mm am/pm`
 * @param date
 */
const formatDateTime = (date: number) => `${format(date, 'M/d/yy')} at ${format(date, 'h:mm a')}`;

/**
 * Format transaction status to a readable text
 * @param status - Transaction status
 */
const formatStatusText = (status: TransactionStatus) => {
    switch (status) {
        case 'WITHDRAWAL':
        case 'PAYMENT_CONFIRMED':
        case 'CREDIT':
        case 'DEBIT':
        case 'CONFIRMED':
            return 'Completed';
        case 'WITHDRAWAL_ATTEMPT':
        case 'PAYMENT_CREATED':
            return i18n.t('transactions:attempted');
        case 'WITHDRAWAL_FAILED':
            return 'Failed';
        case 'PAYMENT_CANCELLED':
        case 'WITHDRAWAL_CANCELLED':
            return 'Cancelled';
        case 'PAYMENT_ABORTED':
            return 'Aborted';
        case 'PAYMENT_DECLINED':
            return 'Declined';
        case 'PAYMENT_EXPIRED':
            return 'Expired';
        case 'PAYMENT_PENDING':
            return 'Pending';
        case 'PAYMENT_PENDING_VERIFICATION':
            return 'Pending Verification';
        case 'PAYMENT_REVERSED':
            return 'Reversed';
    }
};

/**
 * Format the card type for a transaction
 * @param transaction - Transaction object
 */
export const formatCardType = (transaction: Transaction) => {
    switch (transaction.payment_method) {
        case 'paysafe':
            return 'debit card';
        case 'paysafe_mazooma':
            return 'account';
        case 'paysafe_paypal':
            return 'PayPal';
        case 'paysafe_pay_by_bank':
            return 'bank';
        case 'paysafe_venmo':
            return 'Venmo';
        case 'trustly':
            return 'bank account via Trustly';
        case 'fantasy_money':
            return 'Fantasy Wallet';
    }
};

/**
 * Format the card details for a transaction
 * @param transaction - Transaction object
 */
export const formatCardDetails = (transaction: Transaction) => {
    switch (transaction.payment_method) {
        case 'paysafe':
        case 'paysafe_mazooma':
            let fromText = '';
            if (transaction.cc_type) {
                fromText = `${transaction.cc_type} `;
            }
            fromText = `${fromText}${formatCardType(transaction)}`;
            if (transaction.cc_last4) {
                fromText = `${fromText} (•••• ${transaction.cc_last4})`;
            }
            return fromText;
        case 'paysafe_paypal':
            if (transaction.cc_last4) {
                return `Card ending in ${transaction.cc_last4}`;
            }
            if (transaction.email) {
                return transaction.email;
            }
            return 'Paypal';
        case 'paysafe_venmo':
            if (transaction.venmo_id) {
                return `Venmo for ${transaction.venmo_id}`;
            }
            return 'Venmo';
        case 'trustly':
            return `${transaction.cc_type} via Trustly (•••• ${transaction.cc_last4})`;
    }
};

/**
 * Format the last 4 digits of a card for a transaction
 * @param transaction - Transaction object
 */
export const formatLast4 = (transaction: Transaction) => {
    return transaction.cc_last4 ? ` ending in ${transaction.cc_last4}` : '';
};

const statusOrder = ['requested', 'reviewed', 'complete'] as const;

/**
 * Formats the transaction `status_events`.
 *
 * @param transaction - The transaction detail object containing `status_events`.
 * @returns An array of formatted status objects with label, status, and completedAt properties.
 */
export const formatTransactionRequestStatus = (transaction: Transaction) => {
    let previousStatus: TransactionRequestStatusType | null = null;

    // If status_events is an array, return an empty object
    const statusEvents = Array.isArray(transaction.status_events) ? {} : transaction.status_events;

    return statusOrder
        .map(key => {
            const statusEvent = statusEvents[key];

            if (!statusEvent) {
                return;
            }

            const { status, completed_at } = statusEvent;

            let formattedStatus: TransactionRequestStatusType = status;

            if (
                status === TRANSACTION_REQUEST_STATUS.PENDING &&
                previousStatus !== TRANSACTION_REQUEST_STATUS.SUCCESS
            ) {
                formattedStatus = 'NONE';
            }

            previousStatus = status;

            return {
                label: toTitleCase(key), // e.g. "Requested", "Reviewed", "Complete"
                status: formattedStatus,
                completedAt:
                    status === TRANSACTION_REQUEST_STATUS.SUCCESS && completed_at
                        ? formatDateTime(completed_at)
                        : undefined,
                testID: `txnDetails-${key}`,
            };
        })
        .filter(event => event !== undefined);
};

/**
 * Format balance based on transaction type
 * @param transaction - Transaction object
 * @param balance - The balance value to format
 */
const formatTransactionBalance = (balance: string | null, displayCurrency: TransactionDisplayCurrency | null) => {
    // Real-money balances keep `$`; sweeps balances drop the word (the icon conveys it).
    if (!displayCurrency) {
        return formatBalance(balance);
    }
    return formatSocialCurrency(balance || 0, toAmountCurrency(displayCurrency));
};
